import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Tooltip } from "@mui/material";
import SectionLoader from "../../../../../component/SectionLoader";
import EmptyState from "../../../../../component/EmptyState";
import { StatusChip, PersonCell, FilterSelect } from "../../../../../component/listing";
import BtnPrimary from "../../../../../component/PrimaryButton";
import {
  getPerformanceAnalytics,
  getReviewCycles,
  getReviewsToDo,
  getAllGoals,
} from "../../../../../services/performance";
import { useNotify } from "../../../../../component/NotificationProvider";
import { tokens, displayFont, chartColors, motion } from "../../../../../theme";
import { pressable } from "../../../../../component/pressableProps";
import {
  ratingBands,
  distribution,
  cycleProgress,
  share,
  reportState,
  reportDetail,
  goalsMet,
  reportsComplete,
  currentCycle,
  deadlineLine,
  shortDate,
  MISSING_STAGES,
} from "./performanceFacts";

/**
 * One stage of the cycle, as a labelled bar.
 *
 * The count is the point, not the percentage: "124 / 142" tells a cycle owner
 * how many people to chase, where "87%" does not.
 */
const Stage = ({ label, done, of }) => (
  <Box>
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: "7px" }}>
      <Box sx={{ fontSize: "0.8125rem", color: tokens.text }}>{label}</Box>
      {/* The count is plain, not bold: it is the reading, and the label beside
          it is what tells you what was read. Bolding it makes four rows shout. */}
      <Box
        sx={{
          fontSize: "0.8125rem",
          fontVariantNumeric: "tabular-nums",
          color: tokens.textMuted,
        }}
      >
        {done} / {of}
      </Box>
    </Stack>
    <Box sx={{ height: "8px", borderRadius: "4px", background: tokens.border, overflow: "hidden" }}>
      <Box
        sx={{
          width: `${share(done, of) * 100}%`,
          height: "100%",
          background: tokens.primary,
          transition: `width ${motion.duration[3]}ms ${motion.easing.decelerate}`,
        }}
      />
    </Box>
  </Box>
);

/** The distribution's three rows share one column rhythm. */
const COLS = { display: "flex", gap: "14px" };

/**
 * The rating distribution, as columns.
 *
 * This is the one place in PayLoom the categorical palette earns its place: a
 * distribution is a set of unordered categories, and colouring them apart is
 * what makes the shape readable. It is also the only place — no individual is
 * ever coloured by their rating, which is why nothing below this component
 * receives a colour.
 */
const Distribution = ({ dist }) => (
  <Box>
    {/* The counts sit in their own row above the bars rather than riding on top
        of each column: a number pinned to a 2%-tall bar and a number pinned to a
        full-height one land at different heights, and the row of figures stops
        being readable as a row. */}
    <Box
      sx={{
        ...COLS,
        mb: "8px",
        fontSize: "0.75rem",
        fontVariantNumeric: "tabular-nums",
        color: tokens.textMuted,
      }}
    >
      {dist.rows.map((r) => (
        <Box key={r.score} sx={{ flex: 1, textAlign: "center" }}>{r.count}</Box>
      ))}
    </Box>
    <Box sx={{ ...COLS, alignItems: "flex-end", height: "150px", mb: "14px" }}>
      {dist.rows.map((r) => (
        <Tooltip key={r.score} title={`${r.label} · ${r.count} of ${dist.total}`}>
          <Box
            sx={{
              flex: 1,
              height: dist.max ? `${Math.max(2, (r.count / dist.max) * 100)}%` : "2px",
              background: chartColors[r.colorIndex % chartColors.length],
              borderRadius: "4px 4px 0 0",
              transition: `height ${motion.duration[3]}ms ${motion.easing.decelerate}`,
            }}
          />
        </Tooltip>
      ))}
    </Box>
    <Box sx={COLS}>
      {dist.rows.map((r) => (
        <Box
          key={r.score}
          sx={{ flex: 1, textAlign: "center", fontSize: "0.6875rem", color: tokens.textMuted, lineHeight: 1.3 }}
        >
          {r.label}
        </Box>
      ))}
    </Box>
  </Box>
);

/**
 * A panel inside the dashboard card.
 *
 * The title is a small-caps label, not a heading: these are the parts of one
 * screen, and a 17px heading on each of them competes with the cycle name that
 * the whole card is about. `suffix` joins the title on the same line ("Rating
 * distribution · 41 completed") because it qualifies the label rather than
 * standing beside it; `right` is a separate reading, so it sits opposite.
 */
const Panel = ({ title, suffix, right, note, children, sx }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: "20rem",
      background: tokens.card,
      border: `1px solid ${tokens.border}`,
      borderRadius: "10px",
      p: "22px",
      ...sx,
    }}
  >
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={1}
      sx={{ mb: note ? "0.5rem" : "1.25rem" }}
    >
      <Box
        sx={{
          fontSize: "0.8125rem",
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: tokens.textMuted,
        }}
      >
        {suffix ? `${title} · ${suffix}` : title}
      </Box>
      {right && <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{right}</Box>}
    </Stack>
    {note && <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "1.25rem" }}>{note}</Box>}
    {children}
  </Box>
);

const PerformanceDashboard = ({ onStartReview }) => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [data, setData] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [cycleId, setCycleId] = useState("");
  const [reports, setReports] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchData = useCallback(async (id) => {
    setLoading(true);
    setError(false);
    try {
      const [analytics, cycleRes, toDo, goalRes] = await Promise.all([
        getPerformanceAnalytics(id || undefined),
        getReviewCycles(),
        getReviewsToDo(),
        getAllGoals(id ? { reviewCycle: id } : {}),
      ]);
      if (analytics?.success === false) throw new Error(analytics?.message);
      setData(analytics?.data || null);
      setCycles(cycleRes?.data || []);
      setReports(toDo?.data || []);
      setGoals(goalRes?.data || []);
    } catch (e) {
      setError(true);
      notify.error(e?.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { fetchData(cycleId); }, [fetchData, cycleId]);

  // The cycle in view: whatever the picker holds, else the active one.
  const cycle = useMemo(
    () => cycles.find((c) => String(c._id) === String(cycleId)) || currentCycle(cycles),
    [cycles, cycleId]
  );

  const bands = useMemo(() => ratingBands(cycle), [cycle]);
  const dist = useMemo(
    () => distribution(data?.ratingDistribution, bands),
    [data, bands]
  );
  const progress = useMemo(() => cycleProgress(data?.reviewFunnel), [data]);
  const done = useMemo(() => reportsComplete(reports), [reports]);

  if (loading) return <SectionLoader />;
  if (error || !data) return <EmptyState variant="error" title="Couldn't load dashboard" onRetry={() => fetchData(cycleId)} />;

  const { dueForConfirmation = [] } = data;
  const deadline = deadlineLine(cycle);

  return (
    <Stack spacing={3}>
      {/* The cycle is the subject of the screen, so it names it. */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ md: "flex-start" }}
        spacing={2.5}
      >
        <Box>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.5rem", fontWeight: 700, color: tokens.text }}>
            {cycle?.name || "No review cycle"}
          </Box>
          <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, mt: "4px" }}>
            {cycle
              ? [deadline, progress.awaitingSelf ? `${progress.awaitingSelf} yet to submit a self-review` : null]
                  .filter(Boolean)
                  .join(" · ") || "No deadline set"
              : "Create a cycle to start reviewing."}
          </Box>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {cycles.length > 1 && (
            <FilterSelect
              value={cycleId}
              onChange={(e) => setCycleId(e.target.value)}
              options={[
                { value: "", label: "Current cycle" },
                ...cycles.map((c) => ({ value: c._id, label: c.name })),
              ]}
              inputProps={{ "aria-label": "Review cycle" }}
            />
          )}
          {onStartReview && (
            <BtnPrimary onClick={onStartReview} sx={{ borderRadius: "8px" }}>
              Start a review
            </BtnPrimary>
          )}
        </Stack>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2.5}>
        <Panel
          title="Cycle progress"
          note={
            // Two of the book's four stages have nothing behind them, and a bar
            // invented from no data reads as a measurement. Naming them is the
            // honest alternative to drawing them.
            `${MISSING_STAGES.map((s) => s.label).join(" and ")} are not tracked yet`
          }
        >
          {progress.total === 0 ? (
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
              No reviews have been generated for this cycle.
            </Box>
          ) : (
            <Stack spacing="16px">
              {progress.stages.map((s) => <Stage key={s.key} {...s} />)}
            </Stack>
          )}
        </Panel>

        <Panel title="Rating distribution" suffix={`${dist.total} completed`}>
          {dist.total === 0 ? (
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
              No reviews have been finalised yet, so there is no distribution to show.
            </Box>
          ) : (
            <Distribution dist={dist} />
          )}
        </Panel>
      </Stack>

      <Panel
        title="Your direct reports"
        right={reports.length ? `${done.done} of ${done.of} complete` : null}
      >
        {reports.length === 0 ? (
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
            No reviews are waiting on you.
          </Box>
        ) : (
          <Box>
            {reports.map((r, i) => {
              const state = reportState(r);
              const g = goalsMet(goals, r.employee?._id || r.employee, cycle?._id);
              return (
                <Box
                  key={r._id}
                  {...pressable(() => navigate(`/app/performance/review/${r._id}`))}
                  sx={{
                    display: "grid",
                    // Goals met and where-the-review-is are two separate
                    // readings, so they get two columns rather than being
                    // stacked into the name's subtitle.
                    gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr 1.2fr 130px" },
                    gap: "16px",
                    alignItems: "center",
                    py: "13px",
                    fontSize: "0.875rem",
                    cursor: "pointer",
                    borderTop: i === 0 ? "none" : `1px solid ${tokens.border}`,
                    "&:hover": { background: tokens.surfaceSubtle },
                  }}
                >
                  {/* No individual is ever coloured by their rating. */}
                  <PersonCell
                    name={
                      r.employee
                        ? `${r.employee.fname || ""} ${r.employee.lname || ""}`.trim() || "Unknown"
                        : "Unknown"
                    }
                    src={r.employee?.profileImage}
                    size="30px"
                  />
                  <Box sx={{ color: tokens.textMuted }}>
                    {g.of ? `${g.met} of ${g.of} goals met` : "No goals set"}
                  </Box>
                  <Box sx={{ color: tokens.textMuted }}>{reportDetail(r)}</Box>
                  <Box>
                    <StatusChip label={state.label} tone={state.tone} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Panel>

      {dueForConfirmation.length > 0 && (
        <Panel title="Due for confirmation" right={`${dueForConfirmation.length} past their probation end date`}>
          <Stack>
            {dueForConfirmation.map((e, i) => (
              <Stack
                key={e._id}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                {...pressable(() => navigate(`/app/employee-details/${e._id}/career`))}
                sx={{
                  py: "0.625rem",
                  cursor: "pointer",
                  borderTop: i === 0 ? "none" : `1px solid ${tokens.border}`,
                  "&:hover": { background: tokens.surfaceSubtle },
                }}
              >
                <Box>
                  <Box sx={{ fontSize: "0.875rem", fontWeight: 600, color: tokens.text }}>{e.name}</Box>
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
                    {e.designation || ""}{e.department ? ` · ${e.department}` : ""}
                  </Box>
                </Box>
                <Box sx={{ fontSize: "0.8125rem", color: tokens.warningText, fontWeight: 600 }}>
                  ended {shortDate(e.probationEndDate) || "—"}
                </Box>
              </Stack>
            ))}
          </Stack>
        </Panel>
      )}
    </Stack>
  );
};

export default PerformanceDashboard;
